/* Matrix Client-Server API types — only the slices the client actually uses. */

export interface MatrixSession {
  baseUrl: string
  userId: string
  deviceId: string
  accessToken: string
}

export interface Discovery {
  baseUrl: string
  rtcFocus: string | null
  floorSpace: string | null
  callUrl: string | null
  whiteboard: string | null
}

/** A Matrix event as seen in timelines, state, and to-device streams. */
export interface MatrixEvent<C = Record<string, unknown>> {
  type: string
  sender?: string
  event_id?: string
  state_key?: string
  origin_server_ts?: number
  content: C
  unsigned?: { transaction_id?: string; [k: string]: unknown }
}

export interface StateEvent<C = Record<string, unknown>> extends MatrixEvent<C> {
  state_key: string
}

export interface RoomMessageContent {
  msgtype: string
  body: string
  format?: string
  formatted_body?: string
  'm.mentions'?: { user_ids?: string[]; room?: boolean }
  [k: string]: unknown
}

export interface SyncJoinedRoom {
  timeline?: { events?: MatrixEvent[]; prev_batch?: string; limited?: boolean }
  state?: { events?: MatrixEvent[] }
  account_data?: { events?: MatrixEvent[] }
  ephemeral?: { events?: MatrixEvent[] }
}

export interface SyncResponse {
  next_batch: string
  rooms?: {
    join?: Record<string, SyncJoinedRoom>
    invite?: Record<string, { invite_state?: { events?: MatrixEvent[] } }>
    leave?: Record<string, unknown>
  }
  to_device?: { events?: MatrixEvent[] }
  account_data?: { events?: MatrixEvent[] }
}

export interface JoinedMembers {
  joined: Record<string, { display_name?: string; avatar_url?: string }>
}

export interface MessagesResponse {
  chunk: MatrixEvent[]
  start?: string
  end?: string
}

/** A typed error carrying the Matrix errcode + HTTP status (no more silent catch). */
export class MatrixError extends Error {
  errcode?: string
  httpStatus?: number
  data?: Record<string, unknown>
  constructor(message: string, opts: { errcode?: string; httpStatus?: number; data?: Record<string, unknown> } = {}) {
    super(message)
    this.name = 'MatrixError'
    this.errcode = opts.errcode
    this.httpStatus = opts.httpStatus
    this.data = opts.data
  }
}

/** True when an error means our access token is no longer valid (e.g. the
 *  homeserver was reset) — the only sane response is to sign back in. */
export function isAuthError(err: unknown): boolean {
  if (!(err instanceof MatrixError)) return false
  return (
    err.errcode === 'M_UNKNOWN_TOKEN' ||
    err.errcode === 'M_MISSING_TOKEN' ||
    err.httpStatus === 401
  )
}
